import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Clock, Save, Loader2, Calendar as CalendarIcon, Info } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

/**
 * Compute the earliest delivery date for a given lead-time policy starting today.
 * Mirrors the backend logic in services/app_settings_service.py.
 */
const computeEarliestDelivery = (leadTimeValue, leadTimeUnit, today = new Date()) => {
  if (leadTimeValue <= 0) return today;
  const isBiz = leadTimeUnit === 'business_days';
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let counted = 0;
  while (counted < leadTimeValue) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isBiz || (cursor.getDay() !== 0 && cursor.getDay() !== 6)) {
      counted += 1;
    }
  }
  return cursor;
};

const formatDate = (d) =>
  d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const OrderingDeadlines = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverState, setServerState] = useState(null);
  const [leadTimeValue, setLeadTimeValue] = useState(2);
  const [leadTimeUnit, setLeadTimeUnit] = useState('business_days');
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getOrderingDeadlines();
        setServerState(data);
        setLeadTimeValue(data.lead_time_value);
        setLeadTimeUnit(data.lead_time_unit);
        setMaxAdvanceDays(data.max_advance_days ?? 0);
      } catch {
        toast.error('Failed to load ordering deadlines');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isDirty = useMemo(() => {
    if (!serverState) return false;
    return (
      Number(leadTimeValue) !== serverState.lead_time_value ||
      leadTimeUnit !== serverState.lead_time_unit ||
      Number(maxAdvanceDays) !== (serverState.max_advance_days ?? 0)
    );
  }, [serverState, leadTimeValue, leadTimeUnit, maxAdvanceDays]);

  const earliestDelivery = useMemo(
    () => computeEarliestDelivery(Number(leadTimeValue) || 0, leadTimeUnit),
    [leadTimeValue, leadTimeUnit]
  );

  // Preview furthest bookable date (mirrors backend `advance_horizon_boundary`).
  const furthestBookable = useMemo(() => {
    const n = Number(maxAdvanceDays) || 0;
    if (n <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d;
  }, [maxAdvanceDays]);

  const handleSave = async () => {
    const value = Number(leadTimeValue);
    if (!Number.isInteger(value) || value < 0 || value > 30) {
      toast.error('Lead time must be a whole number between 0 and 30.');
      return;
    }
    const cap = Number(maxAdvanceDays);
    if (!Number.isInteger(cap) || cap < 0 || cap > 365) {
      toast.error('Maximum advance window must be a whole number between 0 and 365.');
      return;
    }
    setSaving(true);
    try {
      const data = await adminService.updateOrderingDeadlines({
        lead_time_value: value,
        lead_time_unit: leadTimeUnit,
        max_advance_days: cap,
      });
      setServerState(data);
      toast.success('Ordering deadlines saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!serverState) return;
    setLeadTimeValue(serverState.lead_time_value);
    setLeadTimeUnit(serverState.lead_time_unit);
    setMaxAdvanceDays(serverState.max_advance_days ?? 0);
  };

  return (
    <div className="space-y-6" data-testid="ordering-deadlines-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="h-6 w-6 text-amber-600" />
          Ordering Deadlines
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Configure how far in advance clients must place wholesale orders before delivery.
          This is a global setting that applies to every client and is enforced both when an
          order is created and when it is edited.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lead Time</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="lead-time-value">Lead time</Label>
                  <Input
                    id="lead-time-value"
                    type="number"
                    min={0}
                    max={30}
                    step={1}
                    value={leadTimeValue}
                    onChange={(e) => setLeadTimeValue(e.target.value)}
                    data-testid="lead-time-value-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">A whole number between 0 and 30.</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="lead-time-unit">Unit</Label>
                  <Select value={leadTimeUnit} onValueChange={setLeadTimeUnit}>
                    <SelectTrigger id="lead-time-unit" data-testid="lead-time-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business_days" data-testid="unit-business-days">
                        Business days (skip weekends)
                      </SelectItem>
                      <SelectItem value="calendar_days" data-testid="unit-calendar-days">
                        Calendar days (every day counts)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Pick how the lead time is counted between today and the delivery date.
                  </p>
                </div>
              </div>

              {/* Live preview */}
              <div
                className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
                data-testid="lead-time-preview"
              >
                <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-amber-900">Preview</p>
                  <p className="text-amber-900/80">
                    With <strong>{leadTimeValue || 0} {leadTimeUnit === 'business_days' ? 'business' : 'calendar'} {Number(leadTimeValue) === 1 ? 'day' : 'days'}</strong> of lead time,
                    an order placed today (
                    {formatDate(new Date())}) can be delivered as early as{' '}
                    <strong data-testid="lead-time-preview-date">{formatDate(earliestDelivery)}</strong>.
                  </p>
                  <p className="text-xs text-amber-800/70">
                    Note: clients are also restricted to their own configured delivery days
                    (Mon/Wed/Thu/Sat etc), which may push the actual earliest delivery further.
                  </p>
                </div>
              </div>

              {/* Maximum advance window — the ceiling to the lead-time's floor.
                  0 means no cap (feature disabled). Recurring schedules,
                  admin-placed orders, and delivery_override dates bypass. */}
              <div className="pt-6 border-t space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Maximum advance ordering window</h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    How far into the future clients can place ad-hoc orders. This is the ceiling
                    counterpart of the lead time above. Recurring schedules, orders you place on
                    a client&apos;s behalf, and dates you deliberately open via a menu override
                    are exempt.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="max-advance-days">Maximum advance (calendar days)</Label>
                    <Input
                      id="max-advance-days"
                      type="number"
                      min={0}
                      max={365}
                      step={1}
                      value={maxAdvanceDays}
                      onChange={(e) => setMaxAdvanceDays(e.target.value)}
                      data-testid="max-advance-days-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A whole number between 0 and 365. <strong>0 disables the cap</strong> — clients can book any deliverable date.
                    </p>
                  </div>
                  <div
                    className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
                    data-testid="max-advance-preview"
                  >
                    <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm text-amber-900/90">
                      {Number(maxAdvanceDays) > 0 ? (
                        <>
                          <p className="font-medium text-amber-900">Preview</p>
                          <p>
                            Clients can book deliveries up to <strong>{formatDate(furthestBookable)}</strong> —
                            {' '}{Number(maxAdvanceDays)} calendar {Number(maxAdvanceDays) === 1 ? 'day' : 'days'} from today.
                            Dates beyond that will appear disabled on the Shop calendar with a tooltip.
                          </p>
                        </>
                      ) : (
                        <p className="font-medium text-amber-900">
                          No advance limit — clients can book any deliverable date.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Last updated */}
              {serverState?.updated_at && (
                <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="lead-time-meta">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Last updated {formatDateTime(serverState.updated_at)}
                  {serverState.updated_by_email && (
                    <Badge variant="outline" className="ml-1">
                      {serverState.updated_by_email}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2 border-t">
                <Button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="save-deadlines-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRevert}
                  disabled={!isDirty || saving}
                  data-testid="revert-deadlines-btn"
                >
                  Revert
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderingDeadlines;
